import React from "react";
import { Show } from "@refinedev/antd";
import { useShow } from "@refinedev/core";
import { Descriptions, Tag, Space, Card } from "antd";
import { REQUEST_TEMPLATE_ONE_QUERY } from "./queries";
import WorkflowDiagram from "./components/WorkflowDiagram";

interface IApprovalStep {
  order: number;
  roleSlug: string;
  stepName?: string;
}

interface IPurchaseTemplate {
  id: string;
  name: string;
  templateKind?: 'REQUEST' | 'ORDER' | 'RECEIPT' | 'INSPECTION' | 'INVOICE' | 'PLAN' | 'PLAN_REPORT';
  teamId?: string;
  fiscalYear?: number;
  isActive: boolean;
  approvalType?: 'LOCAL' | 'EXTERNAL';
  externalWorkflow?: any;
  steps: IApprovalStep[];
  createdAt: string;
  updatedAt: string;
}

export const ShowPurchaseTemplate: React.FC = () => {
  const { query } = useShow<IPurchaseTemplate>({
    resource: "purchaseTemplates",
    meta: { gqlQuery: REQUEST_TEMPLATE_ONE_QUERY },
  });

  const record = (query?.data?.data as any) || {};

  const approvalType = record.approvalType || 'EXTERNAL';

  return (
    <Show title="Purchase Template">
      <Descriptions bordered size="small" column={1}>
        <Descriptions.Item label="Name">{record.name}</Descriptions.Item>
        <Descriptions.Item label="Template Type">{record.templateKind || "REQUEST"}</Descriptions.Item>
        <Descriptions.Item label="Team">{record.teamId || "—"}</Descriptions.Item>
        <Descriptions.Item label="Fiscal Year">{record.fiscalYear ?? "—"}</Descriptions.Item>
        <Descriptions.Item label="Approval Type">
          <Tag color={approvalType === 'EXTERNAL' ? 'geekblue' : 'gold'}>
            {approvalType === 'EXTERNAL' ? 'External Approval Flow' : 'Local Approval'}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Active">
          <Tag color={record.isActive ? "success" : "default"}>
            {record.isActive ? "Active" : "Inactive"}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Created At">{record.createdAt}</Descriptions.Item>
        <Descriptions.Item label="Updated At">{record.updatedAt}</Descriptions.Item>
      </Descriptions>

      {approvalType === 'EXTERNAL' ? (
        <Card title="Approval Flow" style={{ marginTop: 16 }}>
          {record.externalWorkflow ? (
            <WorkflowDiagram flow={record.externalWorkflow} />
          ) : (
            <span style={{ color: "#888" }}>No workflow selected.</span>
          )}
        </Card>
      ) : (
        <Card title="Approval Steps" style={{ marginTop: 16 }}>
          {Array.isArray(record.steps) && record.steps.length ? (
            <Space wrap>
              {[...record.steps]
                .sort((a: IApprovalStep, b: IApprovalStep) => Number(a.order ?? 0) - Number(b.order ?? 0))
                .map((s: IApprovalStep) => (
                  <Tag key={`${s.roleSlug}-${s.order}`} color="blue">
                    {s.order}: {s.stepName || s.roleSlug}
                  </Tag>
                ))}
            </Space>
          ) : (
            <span style={{ color: "#888" }}>No steps defined.</span>
          )}
        </Card>
      )}
    </Show>
  );
};